// Creative Studio upload intake helpers.
//
// Pure, framework-free helpers for validating and partitioning files selected
// for upload (via file picker or drag-and-drop). Kept separate from the page so
// the single + bulk intake rules can be unit tested in isolation.

// Mirrors the backend multer ceiling (routes/creativeAssets.js: 750MB per file).
export const MAX_CREATIVE_UPLOAD_BYTES = 750 * 1024 * 1024;

// Human-readable accepted-format + size hint shown under the uploader.
export const CREATIVE_UPLOAD_ACCEPT_HINT = "Images & video · up to 750MB each";

// The accept attribute for the hidden file input.
export const CREATIVE_UPLOAD_ACCEPT_ATTR = "image/*,video/*";

const CREATIVE_UPLOAD_EXTENSION_RE = /\.(png|jpe?g|gif|webp|bmp|svg|mp4|mov|m4v|webm|avi|mkv)$/i;

export function isAllowedCreativeUploadFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "");
  return (
    type.startsWith("image/") ||
    type.startsWith("video/") ||
    CREATIVE_UPLOAD_EXTENSION_RE.test(name)
  );
}

export function isOversizedCreativeUploadFile(file) {
  const size = Number(file?.size);
  return Number.isFinite(size) && size > MAX_CREATIVE_UPLOAD_BYTES;
}

// Split a FileList / array into accepted files and rejected entries (with a
// reason), so the UI can keep valid files and report the bad ones — one bad
// file never blocks the rest of the batch.
export function partitionCreativeUploadFiles(input) {
  const files = input ? Array.from(input) : [];
  const accepted = [];
  const rejected = [];

  for (const file of files) {
    if (!isAllowedCreativeUploadFile(file)) {
      rejected.push({ name: String(file?.name || "file"), reason: "type" });
      continue;
    }
    if (isOversizedCreativeUploadFile(file)) {
      rejected.push({ name: String(file?.name || "file"), reason: "size" });
      continue;
    }
    accepted.push(file);
  }

  return { accepted, rejected };
}

// Build a concise, secret-free message describing rejected files.
export function describeRejectedCreativeUploads(rejected) {
  const list = Array.isArray(rejected) ? rejected : [];
  if (!list.length) return "";
  const typeRejects = list.filter((entry) => entry.reason === "type").map((entry) => entry.name);
  const sizeRejects = list.filter((entry) => entry.reason === "size").map((entry) => entry.name);
  const parts = [];
  if (typeRejects.length) {
    parts.push(
      `${typeRejects.length === 1 ? "Unsupported file" : "Unsupported files"}: ${typeRejects.join(", ")} (choose images or video).`
    );
  }
  if (sizeRejects.length) {
    parts.push(
      `${sizeRejects.length === 1 ? "File too large" : "Files too large"}: ${sizeRejects.join(", ")} (max 750MB each).`
    );
  }
  return parts.join(" ");
}
